import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

const LAST_SEEN_KEY = "bug_report_last_seen";

function getLastSeen(): string {
  return localStorage.getItem(LAST_SEEN_KEY) || new Date(0).toISOString();
}

function updateLastSeen(): void {
  localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString());
}

/**
 * Checks for the operator's own bug reports that have been resolved since
 * they last acknowledged them. Shows a toast notification for each and
 * returns an unresolved count for badge display.
 */
export function useBugReportNotifications() {
  const { user } = useAuth();
  const lastSeen = useMemo(() => getLastSeen(), []);

  // Query for resolved reports newer than last acknowledgement
  const { data: resolvedReports = [] } = useQuery({
    queryKey: ["bug-report-resolved", user?.id, lastSeen],
    queryFn: async () => {
      if (!user?.id) return [];
      try {
        const { data, error } = await supabase
          .from("error_logs")
          .select("id, message, resolved_at")
          .eq("error_type", "user_report")
          .eq("user_id", user.id)
          .eq("status", "resolved")
          .gt("resolved_at", lastSeen)
          .order("resolved_at", { ascending: false })
          .limit(10);

        if (error) return [];
        return data || [];
      } catch {
        // Network/CORS-level fetch failures should not disrupt normal app UX.
        return [];
      }
    },
    enabled: !!user?.id,
    staleTime: 60_000, // Check once per minute
    refetchOnWindowFocus: true,
  });

  // Show toast notifications for newly resolved reports
  useEffect(() => {
    if (resolvedReports.length === 0) return;

    // Small delay so it doesn't fire during initial page load
    const timer = setTimeout(() => {
      // Mark as seen FIRST so a crash during toasts won't replay them all
      updateLastSeen();
      for (const report of resolvedReports) {
        const preview = report.message.length > 60
          ? report.message.slice(0, 60) + "..."
          : report.message;
        toast.success(`Your report has been resolved: "${preview}"`);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [resolvedReports]);

  // Count of user's own unresolved reports (for badge)
  const { data: unresolvedCount = 0 } = useQuery({
    queryKey: ["bug-report-unresolved-count", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;
      try {
        const { count, error } = await supabase
          .from("error_logs")
          .select("id", { count: "exact", head: true })
          .eq("error_type", "user_report")
          .eq("user_id", user.id)
          .in("status", ["new", "acknowledged", "investigating"]);

        if (error) return 0;
        return count || 0;
      } catch {
        return 0;
      }
    },
    enabled: !!user?.id,
    staleTime: 60_000,
  });

  return { unresolvedCount };
}
